import React, { useEffect, useReducer, useState } from 'react'
import PropTypes from 'prop-types'
import i18n from 'simple-react-i18n'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { Button, Card, CardContent, Chip, Grid2, Typography } from '@mui/material'
import UserAction from '../user/actions/UserAction'
import useProgressDispatch from 'utils/customHook/useProgressDispatch'
import ProgressCard from 'components/card/ProgressCard'
import { isNil, isObject, partition, template } from 'lodash'
import StepperDialog from 'components/modal/StepperDialog'
import visualData from 'assets/pictures/observatory/visual-data.svg'
import UserModulesPanel from '../user/components/detailPanel/UserModulesPanel'
import UserAccessPanel from '../user/components/detailPanel/UserAccessPanel'
import AdministrationAction from 'administration/actions/AdministrationAction'
import CustomizationParameters from './CustomizationParameters'
import ArrowOutwardRoundedIcon from '@mui/icons-material/ArrowOutwardRounded'
import ToastrAction from 'toastr/actions/ToastrAction'
import AccountAction from 'account/actions/AccountAction'
import { usedUrl } from 'conf/basepath'
import { DISTRIBUTION, HYDRO, PIEZO, PLUVIO, PRODUCTION, QUALITO, RESOURCE } from '../user/constants/ApplicationHabilitationConstants'
import UserAssignmentPanel from './UserAssignmentPanel'
import DefinitionPanel from './DefinitionPanel'
import { ModuleHabsContext, ModuleHabsDispatchContext } from './AdminModulesApp'
import { MODULE_PARAMETER } from './ObservatoryConstants'

const DEFINITION_STEP = 0
const ALLOCATION_STEP = 1
const RIGHTS_STEP = 2
const USERS_STEP = 3
const CUSTOMIZATION_STEP = 4

const initialHabsState = {
    habilitationsUser: [],
    stationsUser: [],
    parameters: [],
    moduleParameters: [],
    modules: [
        QUALITO,
        PIEZO,
        PLUVIO,
        HYDRO,
        DISTRIBUTION,
        PRODUCTION,
        RESOURCE,
    ],
    selectedUsersModule: [],
    moduleUrlError: false,
}

const habsReducer = (state, action) => {
    switch (action.type) {
        case 'habilitationsUser':
            return { ...state, habilitationsUser: action.payload }
        case 'stationsUser':
            return { ...state, stationsUser: action.payload }
        case 'stationsHabilitationsUser':
            return { ...state, ...action.payload }
        case 'parameters':
            return { ...state, parameters: action.payload }
        case 'moduleParameters':
            return { ...state, moduleParameters: action.payload }
        case 'selectedUsersModule':
            return { ...state, selectedUsersModule: action.payload }
        case 'moduleUrlError':
            return { ...state, moduleUrlError: action.payload }
        case 'reset':
            return initialHabsState
        default:
            return state
    }
}

const ModuleDialog = ({
    open = false,
    onClose = () => {},

    mod = {},
}) => {
    const {
        applicativeUserSettings,
    } = useSelector(store => ({
        applicativeUserSettings: store.AdministrationReducer.applicativeUserSettings,
    }), shallowEqual)

    const [module, setModule] = useState(mod)

    const [habsState, habsDispatch] = useReducer(habsReducer, initialHabsState)

    const dispatch = useDispatch()

    useEffect(() => {
        if (open) {
            setModule(mod)
        } else {
            habsDispatch({ type: 'reset' })
            dispatch(AdministrationAction.fetchGlobalParameters())
        }
    }, [open])

    const { isLoaded, progress } = useProgressDispatch(() => open ? [
        dispatch(UserAction.fetchUser(mod.login)),
        dispatch(AdministrationAction.fetchUserSettings(mod.login, true)),
        dispatch(UserAction.fetchApplicationHabilitations()),
        dispatch(UserAction.getUsersModule(mod.login, payload => habsDispatch({ type: 'selectedUsersModule', payload }))),
    ] : [], [open])

    const isNew = !!mod.id

    const onValidate = () => {
        const usersModuleLogins = habsState.selectedUsersModule.map(({ login }) => login)
        if (habsState.habilitationsUser.length) {
            dispatch(UserAction.updateUserHabilitations(mod.login, habsState.habilitationsUser))
        }
        if (habsState.stationsUser.length) {
            dispatch(UserAction.updateUserStations(mod.login, habsState.stationsUser))
        }
        if (usersModuleLogins.length) {
            dispatch(UserAction.updateUsersModule(mod.login, usersModuleLogins))
        }

        const [parametersToDelete, otherParameters] = partition(habsState.parameters.filter(p => !isNil(p.isNew)), param => isNil(param.value))
        const [newParameters, parameters] = partition(otherParameters.map(p => ({ ...p, value: isObject(p.value) ? JSON.stringify(p.value) : `${p.value}` })), param => param.isNew)
        if (newParameters.length) {
            dispatch(AdministrationAction.createGlobalParameters(newParameters))
        }
        if (parameters.length) {
            dispatch(AdministrationAction.updateGlobalParameters(parameters))
        }
        if (parametersToDelete.length) {
            dispatch(AdministrationAction.deleteGlobalParameters(parametersToDelete))
        }

        const moduleParameters = habsState.moduleParameters.filter(p => !isNil(p.value)).map(p => ({ ...p, value: isObject(p.value) ? JSON.stringify(p.value) : `${p.value}` }))
        if (moduleParameters.length) {
            dispatch(AccountAction.updateSettings(mod.login, moduleParameters, true, false, () => dispatch(UserAction.getApplicativeUsersParameters())))
        }

        if (habsState.habilitationsUser.length
            || habsState.stationsUser.length
            || usersModuleLogins.length
            || newParameters.length
            || parameters.length
            || parametersToDelete.length
            || moduleParameters.length) {
            dispatch(ToastrAction.success(i18n.moduleSuccessfullySaved))
        }
        onClose()
    }

    const settingModuleParam = applicativeUserSettings.filter(aus => aus.login === module.login).find(p => p.parameter === MODULE_PARAMETER)?.value
    const moduleParam = habsState.moduleParameters.find(p => p.parameter === MODULE_PARAMETER)

    return (
        <StepperDialog
            open={open}
            closeDialog={onClose}
            maxWidth='xl'
            sx={{ minHeight: '85vh', maxHeight: '85vh' }}
            contentStyle={{ flex: '1 1 0' }}
            title={(
                <Grid2 container justifyContent='space-between' alignItems='center'>
                    <h5>{template(i18n.createOrUpdateModuleTitle)({ action: isNew ? i18n.creation : i18n.modification, module: mod.module })}</h5>
                    {usedUrl.includes('dev.sieau.fr') && (
                        <Chip
                            label={i18n.seeWiki}
                            icon={<ArrowOutwardRoundedIcon />}
                            onClick={() => window.open('https://wiki.aquasys.fr/fr/observatoire/administration', '_blank').focus()}
                            clickable
                            variant='new-filled'
                        />
                    )}
                </Grid2>
            )}
            steps={[{
                label: i18n.definition,
                constant: DEFINITION_STEP,
                nextAvailable: (!!module.name && !module.nameError),
            }, {
                label: i18n.moduleAllocation,
                constant: ALLOCATION_STEP,
            }, {
                label: i18n.accessAllocation,
                constant: RIGHTS_STEP,
            }, {
                label: i18n.usersAllocation,
                constant: USERS_STEP,
            }, {
                label: i18n.customization,
                constant: CUSTOMIZATION_STEP,
            }]}

            renderSaveButton={() => (
                <Button
                    variant='contained'
                    onClick={onValidate}
                    disabled={(!module.name || module.nameError) ||
                        (!settingModuleParam && !moduleParam?.value) ||
                        (!!moduleParam && !moduleParam?.isNew && !moduleParam?.value) ||
                        habsState.moduleUrlError
                    }
                >
                    {i18n.validate}
                </Button>
            )}
        >
            {step => (
                <Grid2 container sx={{ height: '100%' }} alignItems='center' columnSpacing={0.5}>
                    <Grid2 container size='grow' sx={{ height: '100%', maxHeight: '100%', overflowY: 'auto', paddingRight: 1 }} rowSpacing={1.5}>
                        {!isLoaded && <Grid2 size={12}><ProgressCard progress={progress} /></Grid2>}
                        <ModuleHabsContext.Provider value={habsState}>
                            <ModuleHabsDispatchContext.Provider value={habsDispatch}>
                                {(isLoaded && step === DEFINITION_STEP) && (
                                    <DefinitionPanel
                                        module={module}
                                        setModule={setModule}
                                    />
                                )}
                                {(isLoaded && step === ALLOCATION_STEP) && (
                                    <Grid2 container size={12} direction='column' rowSpacing={1}>
                                        <Typography variant='sectionTitle'>{i18n.adminModuleAllocationSubtitle}</Typography>
                                        <Card variant='newCard'>
                                            <CardContent>
                                                <UserModulesPanel />
                                            </CardContent>
                                        </Card>
                                    </Grid2>
                                )}
                                {(isLoaded && step === RIGHTS_STEP) && (
                                    <Grid2 container size={12} direction='column' rowSpacing={1}>
                                        <Typography variant='sectionTitle'>{i18n.adminModuleRightSubtitle}</Typography>
                                        <Card variant='newCard'>
                                            <CardContent>
                                                <UserAccessPanel />
                                            </CardContent>
                                        </Card>
                                    </Grid2>
                                )}
                                {(isLoaded && step === USERS_STEP) && (
                                    <Grid2 container size={12} direction='column' rowSpacing={1}>
                                        <Typography variant='sectionTitle'>{i18n.adminModuleUserSubtitle}</Typography>
                                        <UserAssignmentPanel />
                                    </Grid2>
                                )}
                                {(isLoaded && step === CUSTOMIZATION_STEP) && (
                                    <CustomizationParameters module={module} />
                                )}
                            </ModuleHabsDispatchContext.Provider>
                        </ModuleHabsContext.Provider>
                    </Grid2>
                    <Grid2 size={2.75} sx={{ '& object': { height: 'auto', width: '100%' } }}>
                        <object data={visualData} type='image/svg+xml' />
                    </Grid2>
                </Grid2>
            )}
        </StepperDialog>
    )
}

ModuleDialog.propTypes = {
    open: PropTypes.bool,
    onClose: PropTypes.func,
    onValidate: PropTypes.func,

    mod: PropTypes.shape({
        login: PropTypes.string,
        moduleTypeId: PropTypes.number,
    }),
    moduleType: PropTypes.string,
}

export default ModuleDialog